import { memo, useMemo, useState } from 'react'
import { Box, Chip, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import PollRoundedIcon from '@mui/icons-material/PollRounded'
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded'
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import FormatQuoteRoundedIcon from '@mui/icons-material/FormatQuoteRounded'
import { useTranslation } from 'react-i18next'
import type {
  AnswerDistribution,
  AnswerQuestion,
  RespondentAnswer,
} from '@/services/emotionAnalysisService'

/**
 * Survey results below the emotion timeline. In "average" mode each choice
 * question shows a single divided horizontal bar (response frequency by colour)
 * with a colour-keyed legend below, across the *filtered* respondent set
 * (so the right-hand filters narrow it too); drilling into one respondent flips
 * every card to show that person's own answers. Free-text questions list the
 * collected answers (average) or the respondent's note (drill-in).
 */

// Distinct, theme-friendly palette for answer choices (cycled per question).
const ANSWER_PALETTE = [
  '#EA5048', '#E8A33D', '#4C9F70', '#5C7AB0', '#7A6FBE',
  '#C77DA4', '#3D9BA8', '#B5843A', '#8E3B36', '#6B7280',
]

const pct = (v: number) => `${Math.round(v * 100)}%`

/**
 * Detect numeric-scale questions (scale 1–5, NPS 0–10): consecutive choices
 * whose label is just their own value. These render as a compact dot scale
 * (drill-in) or a small bar histogram (average) instead of a labelled list.
 */
function numericScale(q: AnswerQuestion) {
  const ch = q.choices
  if (ch.length < 5) return null
  const consecutiveNumbers = ch.every(
    (c, i) => c.text === String(c.value) && (i === 0 || c.value === ch[i - 1].value + 1),
  )
  return consecutiveNumbers ? ch : null
}

function QuestionResultsPanel({
  questions,
  distribution,
  respondentAnswers,
  respondentId,
  respondentLabel,
  respondentCount,
}: {
  questions: AnswerQuestion[]
  distribution: Record<string, AnswerDistribution>
  respondentAnswers: Record<string, RespondentAnswer> | null
  respondentId: number | null
  respondentLabel?: string
  respondentCount: number
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  const individual = respondentId != null

  // Order: real survey questions first, demographics (metryczka) last.
  const ordered = useMemo(
    () => [...questions].sort((a, b) => Number(a.is_meta) - Number(b.is_meta)),
    [questions],
  )

  if (!ordered.length) return null

  const noAverageData =
    !individual && ordered.every((q) => (distribution[String(q.id)]?.responses ?? 0) === 0)
  const noIndividualData = individual && (!respondentAnswers || Object.keys(respondentAnswers).length === 0)

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.85} alignItems="center">
          <Box sx={{ color: c.coral, display: 'grid', placeItems: 'center' }}>
            <PollRoundedIcon fontSize="small" />
          </Box>
          <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{t('results.title')}</Typography>
        </Stack>
        <Chip
          size="small"
          icon={individual ? <PersonOutlineRoundedIcon /> : <GroupsRoundedIcon />}
          label={individual ? respondentLabel ?? t('individual') : t('results.average', { count: respondentCount })}
          color={individual ? 'default' : 'primary'}
          variant={individual ? 'outlined' : 'filled'}
          sx={{ fontWeight: 600 }}
        />
      </Stack>

      {noAverageData || noIndividualData ? (
        <Typography sx={{ color: c.ink56, fontSize: 13.5, py: 2, textAlign: 'center' }}>
          {noIndividualData ? t('results.notCompleted') : t('results.noAnswers')}
        </Typography>
      ) : (
        <Box
          sx={{
            mt: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
          }}
        >
          {ordered.map((q) => (
            <QuestionCard
              key={q.id}
              q={q}
              dist={distribution[String(q.id)]}
              answer={respondentAnswers?.[String(q.id)] ?? null}
              individual={individual}
            />
          ))}
        </Box>
      )}
    </Paper>
  )
}

// ───────────────────────── One question card ─────────────────────────

function QuestionCard({
  q,
  dist,
  answer,
  individual,
}: {
  q: AnswerQuestion
  dist?: AnswerDistribution
  answer: RespondentAnswer | null
  individual: boolean
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const isText = q.type === 'TEXT' || q.choices.length === 0
  const scale = isText ? null : numericScale(q)
  // Average choice distribution → centered header above the divided bar + legend.
  const centeredHeader = !isText && !scale && !individual

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2.5,
        border: `1px solid ${c.ink08}`,
        bgcolor: c.gray50,
        display: 'flex',
        flexDirection: 'column',
        gap: 1.25,
      }}
    >
      <Box sx={{ textAlign: centeredHeader ? 'center' : 'left' }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: c.ink, lineHeight: 1.35 }}>
          {q.text}
        </Typography>
        {q.is_meta && <MetaBadge centered={centeredHeader} />}
      </Box>

      {isText ? (
        <TextAnswers dist={dist} answer={answer} individual={individual} />
      ) : scale ? (
        individual
          ? <ScaleAnswer scale={scale} answer={answer} />
          : <ScaleDistribution scale={scale} dist={dist} />
      ) : individual ? (
        <ChoiceAnswer q={q} answer={answer} />
      ) : (
        <ChoiceDistribution q={q} dist={dist} />
      )}
    </Box>
  )
}

function MetaBadge({ centered }: { centered?: boolean }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  return (
    <Typography sx={{ fontSize: 10, fontWeight: 700, color: c.ink48, letterSpacing: '.04em', textTransform: 'uppercase', mt: 0.25, textAlign: centered ? 'center' : 'left' }}>
      {t('filters.meta')}
    </Typography>
  )
}

// ───────────────────── Average: divided horizontal bar + legend ─────────────────────

function ChoiceDistribution({ q, dist }: { q: AnswerQuestion; dist?: AnswerDistribution }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  // Hovered choice value, shared between the bar segments and the legend rows.
  const [hovered, setHovered] = useState<number | null>(null)

  const segments = q.choices.map((choice, i) => ({
    value: choice.value,
    text: choice.text,
    count: dist?.counts[String(choice.value)] ?? 0,
    color: ANSWER_PALETTE[i % ANSWER_PALETTE.length],
  }))
  const total = segments.reduce((s, seg) => s + seg.count, 0)
  const responses = dist?.responses ?? 0

  if (total === 0) {
    return <Typography sx={{ fontSize: 12.5, color: c.ink56, py: 1, textAlign: 'center' }}>{t('results.noResponse')}</Typography>
  }

  const filled = segments.filter((s) => s.count > 0)
  // Legend lists every option, share-ranked, with zero-count choices last.
  const ranked = [...segments].sort((a, b) => b.count - a.count)
  const dim = (value: number) => hovered != null && hovered !== value

  return (
    <Stack spacing={1.1}>
      {/* Divided horizontal bar — one segment per non-zero choice. */}
      <Box
        sx={{ display: 'flex', gap: '2px', height: 18, width: '100%' }}
        onMouseLeave={() => setHovered(null)}
      >
        {filled.map((seg) => (
          <Box
            key={seg.value}
            title={`${seg.text}: ${seg.count} · ${pct(seg.count / total)}`}
            onMouseEnter={() => setHovered(seg.value)}
            sx={{
              flexGrow: seg.count,
              flexBasis: 0,
              minWidth: 4,
              background: seg.color,
              borderRadius: '4px',
              opacity: dim(seg.value) ? 0.3 : 1,
              transform: hovered === seg.value ? 'scaleY(1.12)' : 'scaleY(1)',
              transition: 'opacity .15s ease, transform .15s ease',
              cursor: 'default',
            }}
          />
        ))}
      </Box>

      {/* Colour-keyed legend for every option. */}
      <Stack spacing={0.2} onMouseLeave={() => setHovered(null)}>
        {ranked.map((seg) => (
          <Stack
            key={seg.value}
            direction="row"
            alignItems="center"
            spacing={0.85}
            onMouseEnter={() => setHovered(seg.value)}
            sx={{
              px: 0.6,
              py: 0.35,
              borderRadius: 1.5,
              bgcolor: hovered === seg.value ? c.ink08 : 'transparent',
              opacity: dim(seg.value) ? 0.45 : 1,
              transition: 'opacity .15s ease, background-color .15s ease',
              cursor: 'default',
            }}
          >
            <Box sx={{ width: 10, height: 10, borderRadius: '3px', background: seg.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 12, color: c.ink72, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {seg.text}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: c.ink, fontVariantNumeric: 'tabular-nums' }}>
              {pct(seg.count / total)}
            </Typography>
          </Stack>
        ))}
      </Stack>

      <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.ink48, letterSpacing: '.03em', textTransform: 'uppercase', textAlign: 'center' }}>
        {responses} {t('results.responsesShort')}
      </Typography>
    </Stack>
  )
}

// ───────────────────────── Drill-in: one respondent's choice ─────────────────────────

function ChoiceAnswer({ q, answer }: { q: AnswerQuestion; answer: RespondentAnswer | null }) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  const selected = new Set(answer?.choices ?? [])

  if (!selected.size) {
    return <Typography sx={{ fontSize: 12.5, color: c.ink56, py: 0.5 }}>{t('results.noResponse')}</Typography>
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
      {q.choices.map((choice, i) => {
        const active = selected.has(choice.value)
        const color = ANSWER_PALETTE[i % ANSWER_PALETTE.length]
        return (
          <Box
            key={choice.value}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.6,
              px: 1.1,
              py: 0.55,
              borderRadius: 999,
              bgcolor: active ? c.coralSoft : 'transparent',
              border: `1px solid ${active ? c.coral : c.ink08}`,
              opacity: active ? 1 : 0.5,
            }}
          >
            {active && (
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  background: color,
                  flexShrink: 0,
                }}
              >
                <CheckRoundedIcon sx={{ fontSize: 10, color: '#fff' }} />
              </Box>
            )}
            <Typography sx={{ fontSize: 12.5, fontWeight: active ? 700 : 500, color: active ? c.ink : c.ink72, whiteSpace: 'nowrap' }}>
              {choice.text}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}

// ───────────────────────── Numeric scale (NPS / 1–5) ─────────────────────────

/** Drill-in: a respondent's pick on a numeric scale, shown as a dot row. */
function ScaleAnswer({
  scale,
  answer,
}: {
  scale: AnswerQuestion['choices']
  answer: RespondentAnswer | null
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')
  const selected = new Set(answer?.choices ?? [])

  if (!selected.size) {
    return <Typography sx={{ fontSize: 12.5, color: c.ink56, py: 0.5 }}>{t('results.noResponse')}</Typography>
  }

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
      {scale.map((choice) => {
        const active = selected.has(choice.value)
        return (
          <Box
            key={choice.value}
            sx={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              bgcolor: active ? c.coral : 'transparent',
              border: `1.5px solid ${active ? c.coral : c.ink32}`,
              opacity: active ? 1 : 0.55,
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: active ? '#fff' : c.ink72, fontVariantNumeric: 'tabular-nums' }}>
              {choice.text}
            </Typography>
          </Box>
        )
      })}
    </Box>
  )
}

/** Average: distribution across a numeric scale, as a compact bar histogram. */
function ScaleDistribution({
  scale,
  dist,
}: {
  scale: AnswerQuestion['choices']
  dist?: AnswerDistribution
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')

  const bars = scale.map((choice) => ({
    value: choice.value,
    label: choice.text,
    count: dist?.counts[String(choice.value)] ?? 0,
  }))
  const total = bars.reduce((s, b) => s + b.count, 0)

  if (total === 0) {
    return <Typography sx={{ fontSize: 12.5, color: c.ink56, py: 1 }}>{t('results.noResponse')}</Typography>
  }

  const max = Math.max(...bars.map((b) => b.count))
  const avg = bars.reduce((s, b) => s + b.value * b.count, 0) / total

  return (
    <Stack spacing={0.75}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.6, height: 72 }}>
        {bars.map((b) => (
          <Box key={b.value} sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4, minWidth: 0 }}>
            <Typography sx={{ fontSize: 9.5, fontWeight: 700, color: c.ink56, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>
              {b.count || ''}
            </Typography>
            <Box
              sx={{
                width: '100%',
                maxWidth: 22,
                height: b.count > 0 ? `${Math.max((b.count / max) * 44, 3)}px` : 2,
                borderRadius: '4px 4px 0 0',
                background: b.count > 0 ? c.coral : c.gray100,
              }}
            />
            <Typography sx={{ fontSize: 10, fontWeight: 600, color: c.ink72, fontVariantNumeric: 'tabular-nums' }}>
              {b.label}
            </Typography>
          </Box>
        ))}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: c.ink48, letterSpacing: '.03em', textTransform: 'uppercase' }}>
        {t('results.avgScore', { value: avg.toFixed(1) })} · {dist?.responses ?? total} {t('results.responsesShort')}
      </Typography>
    </Stack>
  )
}

// ───────────────────────── Free-text answers ─────────────────────────

function TextAnswers({
  dist,
  answer,
  individual,
}: {
  dist?: AnswerDistribution
  answer: RespondentAnswer | null
  individual: boolean
}) {
  const theme = useTheme()
  const c = theme.affexy.colors
  const { t } = useTranslation('emotions')

  const items = individual
    ? answer?.text ? [answer.text] : []
    : dist?.text_answers ?? []

  if (!items.length) {
    return <Typography sx={{ fontSize: 12.5, color: c.ink56, py: 0.5 }}>{t('results.noResponse')}</Typography>
  }

  return (
    <Stack spacing={0.75} sx={{ maxHeight: 168, overflow: 'auto', pr: 0.5 }}>
      {!individual && (
        <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: c.ink48, letterSpacing: '.03em', textTransform: 'uppercase' }}>
          {t('results.textTitle')} · {items.length}
        </Typography>
      )}
      {items.map((text, i) => (
        <Stack key={i} direction="row" spacing={0.75} alignItems="flex-start">
          <FormatQuoteRoundedIcon sx={{ fontSize: 14, color: c.ink32, mt: 0.2, flexShrink: 0 }} />
          <Typography sx={{ fontSize: 12.5, color: c.ink72, lineHeight: 1.45 }}>{text}</Typography>
        </Stack>
      ))}
    </Stack>
  )
}

export default memo(QuestionResultsPanel)
